import useMediaQuery from '@material-ui/core/useMediaQuery'
import { makeStyles } from '@material-ui/styles'

import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import Badge from 'core/elements/badge'
import IconButton from 'core/elements/button/IconButton'
import OverviewActions from 'core/elements/overview/OverviewActions'
import Text from 'core/elements/Text'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'
import React, { useState } from 'react'
import { defaultAppIconPath } from '../constant'
import FailedStatusDetailsDialog from '../FailedStatusDetailsDialog'
import { appStatus, getAppStatus, getResourceDetails } from '../helper'
import InfoBadge from '../InfoBadge'
import InfoBox from '../InfoBox'
import RowInfo from '../RowInfo'

const useStyles = makeStyles<Theme, { showWarningCount: boolean }>((theme) => ({
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr auto',
    alignItems: 'center',
    gap: theme.spacing(2.5),
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 24px',
    gap: '12px',
    background: theme.components.card.background,
    border: `1px solid ${colors.grey[200]}`,
    borderRadius: 4,
    position: 'relative',
  },
  headerContent: {
    width: '100%',
  },
  headerText: {
    display: 'grid',
    gap: theme.spacing(0.5),
    minWidth: 310,
  },
  subContainer: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
  },
  checkContainer: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  content: {
    display: 'grid',
  },
  iconsContainer: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    minWidth: 330,
  },
  resourceContainer: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 330,
  },
  statusContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  divider: {
    width: 1,
    height: 20,
    background: colors.grey[200],
  },
  iconWrapper: {
    width: 56,
    height: 56,
  },
  customButton: {
    '& button': {
      width: 36,
    },
    '& .button-text': {
      gap: 0,
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
    },
  },
  actionContainer: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    '& > div:first-of-type': {
      display: 'flex',
      gap: 24,
      justifyContent: 'normal',
    },
  },
  settingsMenuButton: {
    '&:hover': {
      backgroundColor: colors.grey[200],
    },
  },
  badge: {
    backgroundColor: '#FAFAFC',
    borderColor: '1px solid #E6E6EA',
  },
  icon: {
    minWidth: 50,
  },
}))

const AppListView = ({
  iconName,
  headerName,
  templateName,
  vms,
  status,
  version,
  ownerName,
  overviewActions,
  handleClick,
  isIconView = false,
  data,
}) => {
  const showWarningCount = false
  const isBelow1800 = useMediaQuery('(max-width:1800px)')
  const [showFailedDetails, setShowFailedDetails] = useState(false)
  const classes = useStyles({ showWarningCount })
  const { label, variant } = getAppStatus(status)

  const resourceDetails = getResourceDetails(data, true)

  const templateDetails = [
    {
      label: 'Template',
      value: templateName,
      imagePath: '',
      showDivider: true,
    },
    {
      label: 'Owner',
      value: ownerName,
      imagePath: '',
      showDivider: false,
    },
  ]

  return (
    <>
      {showFailedDetails && data && (
        <FailedStatusDetailsDialog
          rows={[data]}
          onClose={() => {
            setShowFailedDetails(false)
          }}
        />
      )}
      <div className={classes.container}>
        <div className={classes.headerContent}>
          <div className={classes.row}>
            <div className={classes.iconsContainer}>
              <div className={classes.icon}>
                <img src={iconName || defaultAppIconPath} className={classes.iconWrapper} />
              </div>

              <div className={classes.headerText}>
                <Text variant="subtitle2">{headerName}</Text>
                <div className={classes.statusContainer}>
                  <InfoBadge
                    variant={variant}
                    label={label}
                    onInfoClick={() => setShowFailedDetails(true)}
                    showInfo={label === 'Failed' || label === 'Delete Failed'}
                  />
                  <Badge
                    className={classes.badge}
                    variant={appStatus.Default}
                    text={`${vms} VMs`}
                  />
                </div>
              </div>
            </div>
            <div className={classes.resourceContainer}>
              {resourceDetails?.map((item, index) => (
                <InfoBox
                  label={item?.label}
                  value={item?.value}
                  imagePath={item?.imagePath}
                  showDivider={item?.showDivider}
                  isCompact={true}
                />
              ))}

              {isBelow1800 ? (
                <div className={classes.headerText}>
                  {templateDetails?.map((item, index) => (
                    <RowInfo label={item?.label} value={item?.value} />
                  ))}
                </div>
              ) : (
                templateDetails?.map((item, index) => (
                  <InfoBox
                    label={item?.label}
                    value={item?.value}
                    imagePath={item?.imagePath}
                    showDivider={item?.showDivider}
                    // isCompact={true}
                  />
                ))
              )}
            </div>
            <div className={classes.content}>
              <div className={classes.actionContainer}>
                {overviewActions && (
                  <OverviewActions
                    actions={overviewActions}
                    entity={{}}
                    isIconOnly={isIconView}
                    className={isIconView && classes.customButton}
                  />
                )}
                {isAdmin() && (
                  <IconButton
                    size="md"
                    icon="ellipsis-vertical"
                    solid={true}
                    className={classes.settingsMenuButton}
                    onClick={handleClick}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AppListView
